import { GitBranchIcon } from "lucide-react";
import { useCallback, useMemo } from "react";

import {
  decodePinnedVersionSelection,
  encodePinnedVersionSelection,
  type PinnedVersionSelection,
} from "../pinned-version-select";

import { buildPinnedVersionDropdownSections, getPinnedVersionTriggerText } from "./hooks";
import { PinnedVersionSelectContent } from "./VersionDropdownContent";
import type { PinnedVersionSelectorLabel, PinnedVersionSelectorVersion } from "./types";

import { Select, SelectTrigger } from "@/components/ui/select";
import { cn } from "@/lib/utils";

export type PinnedVersionSelectorVariant = "full" | "mini";

export interface PinnedVersionSelectorProps {
  variant?: PinnedVersionSelectorVariant;
  value: PinnedVersionSelection;
  onChange: (value: PinnedVersionSelection) => void;
  versions: PinnedVersionSelectorVersion[];
  labels: PinnedVersionSelectorLabel[];
  /** Tailwind classes for the SelectTrigger (includes width). */
  selectTriggerClassName: string;
  /** Subtitle when selection is "latest" (full variant only). */
  latestDescription?: string;
  /** Subtitle when selection is "label" (full variant only). */
  labelDescription?: string;
  /** Subtitle when selection is "version" (full variant only). */
  versionDescription?: string;
}

export const PinnedVersionSelector = ({
  variant = "full",
  value,
  onChange,
  versions,
  labels,
  selectTriggerClassName,
  latestDescription = "",
  labelDescription = "",
  versionDescription = "",
}: PinnedVersionSelectorProps) => {
  const sections = useMemo(
    () => buildPinnedVersionDropdownSections(versions, labels),
    [versions, labels],
  );
  const encoded = encodePinnedVersionSelection(value);
  const triggerText = useMemo(
    () => getPinnedVersionTriggerText(value, versions, labels),
    [value, versions, labels],
  );

  const handleValueChange = useCallback(
    (next: string) => {
      onChange(decodePinnedVersionSelection(next));
    },
    [onChange],
  );

  if (variant === "mini") {
    return (
      <div className="flex items-center justify-between py-1.5">
        <div className="flex items-center gap-2">
          <GitBranchIcon
            className={cn(
              "h-3.5 w-3.5 transition-colors duration-150",
              value.type === "latest" ? "text-gray-400" : "text-blue-500",
            )}
          />
          <span className="text-xs text-gray-600 dark:text-gray-400">Version</span>
        </div>
        <Select value={encoded} onValueChange={handleValueChange}>
          <SelectTrigger
            className={cn(
              "gap-1 rounded-md border-gray-200 bg-white px-2 text-[11px] shadow-sm focus:ring-1 focus:ring-ring dark:border-gray-700 dark:bg-gray-800",
              selectTriggerClassName,
            )}
          >
            <span className="truncate">{triggerText}</span>
          </SelectTrigger>
          <PinnedVersionSelectContent sections={sections} />
        </Select>
      </div>
    );
  }

  return (
    <div className="group flex items-center justify-between rounded-lg px-3 py-2.5 transition-colors duration-150 hover:bg-gray-50 dark:hover:bg-gray-800/50">
      <div className="flex items-center gap-3">
        <div
          className={cn(
            "flex h-7 w-7 shrink-0 items-center justify-center rounded-lg transition-colors duration-200",
            value.type === "latest"
              ? "bg-gray-100 text-gray-400 dark:bg-gray-800 dark:text-gray-500"
              : "bg-blue-50 text-blue-500 dark:bg-blue-500/10 dark:text-blue-400",
          )}
        >
          <GitBranchIcon className="h-3.5 w-3.5" />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-medium leading-tight text-gray-800 dark:text-gray-200">
            Version
          </p>
          <p className="mt-0.5 text-xs leading-tight text-gray-400 dark:text-gray-500">
            {value.type === "latest" && latestDescription}
            {value.type === "label" && labelDescription}
            {value.type === "version" && versionDescription}
          </p>
        </div>
      </div>
      <Select value={encoded} onValueChange={handleValueChange}>
        <SelectTrigger
          className={cn(
            "gap-1 rounded-md border-gray-200 bg-white px-2 text-xs shadow-sm focus:ring-1 focus:ring-ring dark:border-gray-700 dark:bg-gray-800",
            selectTriggerClassName,
          )}
        >
          <span className="truncate">{triggerText}</span>
        </SelectTrigger>
        <PinnedVersionSelectContent sections={sections} />
      </Select>
    </div>
  );
};
